import { hasOwnProperty, isDefined } from "../../infra3d";
import { Feature, GEOMETRYTYPE } from "../featuretree/internal";
import { MonoplotMeasurePoint, StereoMeasurePoint } from "../measure/internal";
/**
 * Handles the mapping between feature properties and system properties.
 * What are systemproperties? Properties like "campaign", "framenumber", "axis", etc. that are defined in the
 * infra3d-scene or in other objects valid during the session.
 *
 * @class SystempropertyHandler
 */
export class SystempropertyHandler {
    constructor(scene, layer, systemproperties) {
        this._layer = null;
        this._systemproperties = {};
        this.scene = scene;
        this._layer = layer;
        this._systemproperties = systemproperties;
    }
    handleSystemProperties(feature, rule = "onedit", mx, my, info) {
        //The function uses the "systemproperties"-Definition in "layerdefinition.json"
        //Here is an example to map the height-Attribute (for more examples, see wfs/stm_zug/layerdefinition.json)
        //"systemproperties" : [ //X,Y,Z,axisid,u,v,w,axisid1,u1,v1,w2,axisid2,u2,v2,w2
        //       {
        //            "mapping" : {
        //               "HOEHE" : "Z"
        //            },
        //            "rules" : {
        //               "onedit" : "system_to_property",
        //               "onload" : "property_to_system"
        //            }
        //         }
        //      ]
        const sp = this._systemproperties;
        if (sp.length > 0) {
            let props_read = null;
            if (sp.find(function (property) {
                return property.rules[rule].indexOf("system_to_property") >= 0;
            })) {
                props_read = this._getSystemProperties(feature, mx, my, info);
            }
            //only get them if they are needed
            for (let i = 0; i < sp.length; i++) {
                const props_write = {};
                let switchrule = sp[i].rules.onedit;
                if (rule == "onload")
                    switchrule = sp[i].rules.onload;
                for (const attrname in sp[i].mapping) {
                    switch (switchrule) {
                        case "system_to_property":
                            if (isDefined(props_read[sp[i].mapping[attrname]])) {
                                feature.properties[attrname] = props_read[sp[i].mapping[attrname]];
                            }
                            break;
                        case "property_to_system":
                            props_write[sp[i].mapping[attrname]] = feature.properties[attrname];
                            break;
                        case "none":
                            break;
                    }
                }
                this._setSystemProperties(feature, props_write);
            }
        }
    }
    _getSystemProperties(feature, mx, my, measurepoint) {
        //XYZ Properties
        const props = {};
        let coords = [];
        switch (this._layer.geometrytype) {
            case GEOMETRYTYPE.POINT:
                coords = feature.geometry.coordinates;
                break;
            case GEOMETRYTYPE.LINESTRING:
                coords = feature.geometry.coordinates[0];
                props["distance"] = Feature.calcDistance(feature, false).toFixed(2);
                props["horizontalDistance"] = Feature.calcDistance(feature, true).toFixed(2);
                props["heightDifference"] = Feature.calcDeltaHeight(feature).toFixed(2);
                break;
            case GEOMETRYTYPE.POLYGON:
                coords = feature.geometry.coordinates[0][0];
                props["area2D"] = Feature.calcArea(feature, false).toFixed(2);
                props["area3D"] = Feature.calcArea(feature, true).toFixed(2);
                break;
        }
        props["X"] = parseFloat(coords[0].toFixed(3));
        props["Y"] = parseFloat(coords[1].toFixed(3));
        if (isDefined(coords[2])) {
            props["Z"] = parseFloat(coords[2].toFixed(3));
            //linref-Properties simple
            const axis = this.scene.getLRHandler().getActiveNavigationAxis();
            if (axis !== null) {
                const lref = axis.xyz2uvw(props["X"], props["Y"], props["Z"]);
                if (lref !== null) {
                    for (let k = 0; k < axis.idattributearray.length; k++) {
                        props[axis.idattributearray[k]] =
                            axis.axis["features"][lref["featureidx"]]["properties"][axis.idattributearray[k]];
                    }
                    props["u"] = parseFloat(lref.lrcoords[0].toFixed(2));
                    props["v"] = parseFloat(lref.lrcoords[1].toFixed(2));
                    props["w"] = parseFloat(lref.lrcoords[2].toFixed(2));
                }
            }
            //Stream-Properties
            const geoframe = this.scene.getCurrentGeoframe();
            if (geoframe !== null) {
                props["streamid"] = geoframe.stream;
                props["streamname"] = geoframe.getMeasureInfo().name;
                props["dbuser"] = ""; //todo: maybe remove this
                let mp;
                if (isDefined(measurepoint)) {
                    mp = measurepoint;
                }
                else {
                    mp = this.scene
                        .getMeasureHandler()
                        .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), mx, my, 100);
                }
                if (isDefined(mp)) {
                    //bug-fix for ig-tool -> empty db-fields when editing properties in featureinfo-box
                    if (isNaN(mp.frameX) &&
                        isDefined(feature.properties.x_frame) &&
                        isNaN(mp.frameY) &&
                        isDefined(feature.properties.y_frame) &&
                        isNaN(mp.frameZ) &&
                        isDefined(feature.properties.z_frame)) {
                        props["frameX"] = feature.properties.x_frame;
                        props["frameY"] = feature.properties.y_frame;
                        props["frameZ"] = feature.properties.z_frame;
                    }
                    else {
                        props["frameX"] = parseFloat(mp.frameX.toFixed(3));
                        props["frameY"] = parseFloat(mp.frameY.toFixed(3));
                        props["frameZ"] = parseFloat(mp.frameZ.toFixed(3));
                        props["precFrameX"] = parseFloat(mp.precFrameX.toFixed(3));
                        props["precFrameY"] = parseFloat(mp.precFrameY.toFixed(3));
                        props["precFrameZ"] = parseFloat(mp.precFrameZ.toFixed(3));
                    }
                    if (mp instanceof MonoplotMeasurePoint || mp instanceof StereoMeasurePoint) {
                        //bug-fix for ig-tool -> empty db-fields when editing properties in featureinfo-box
                        if (isNaN(mp.imageX) &&
                            isDefined(feature.properties.x) &&
                            isNaN(mp.imageY) &&
                            isDefined(feature.properties.y)) {
                            //this is an ig-module feature
                            props["imageX"] = feature.properties.x;
                            props["imageY"] = feature.properties.y;
                            props["disparity"] = feature.properties.disparity;
                            props["quality"] = feature.properties.quality;
                        }
                        else {
                            props["imageX"] = parseFloat(mp.imageX.toFixed(4));
                            props["imageY"] = parseFloat(mp.imageY.toFixed(4));
                            props["disparity"] = parseFloat(mp.disparity.toFixed(4));
                            props["quality"] = mp.quality;
                            props["precGlobalEasting"] = parseFloat(mp.precGlobalEasting.toFixed(3));
                            props["precGlobalNorthing"] = parseFloat(mp.precGlobalNorthing.toFixed(3));
                            props["precGlobalHeight"] = parseFloat(mp.precGlobalHeight.toFixed(3));
                            props["precLocalEasting"] = parseFloat(mp.precLocalEasting.toFixed(3));
                            props["precLocalNorthing"] = parseFloat(mp.precLocalNorthing.toFixed(3));
                            props["precLocalHeight"] = parseFloat(mp.precLocalHeight.toFixed(3));
                            props["precLocalX"] = parseFloat(mp.precLocalX.toFixed(3));
                            props["precLocalY"] = parseFloat(mp.precLocalY.toFixed(3));
                            props["precLocalZ"] = parseFloat(mp.precLocalZ.toFixed(3));
                        }
                    }
                }
                props["framenumber"] = geoframe.getFramenumber();
                props["datetime"] = geoframe.timestamp;
            }
        }
        return props;
    }
    _setSystemProperties(feature, props) {
        let coords = [];
        if (this._layer.geometrytype == GEOMETRYTYPE.POINT)
            coords = feature.geometry.coordinates;
        //todo: handle all other geometry-types, LineString, Polygon, etc...
        for (const key in props) {
            if (hasOwnProperty(props, key)) {
                //todo: handle all the other props, X,Y,u,v,...ect.
                if (key === "Z" && props[key]) {
                    if (coords.length == 2) {
                        coords.push(parseFloat(props[key]));
                    }
                    else if (coords.length == 3) {
                        coords[2] = parseFloat(props[key]);
                    }
                }
            }
        }
    }
}
//# sourceMappingURL=systempropertyhandler.js.map